import { supabase } from '@/utils/supabase';

export interface MarketingSourceCoverage {
  from: string | null;
  to: string;
  observedAt: string;
  status: 'complete' | 'limited' | 'unavailable';
  reason: string;
}

/** Creation times are immutable; never page on mutable status/update times. */
export async function readMarketingReportSource(
  table: string,
  to: string,
  creationColumn = 'created_at',
  isCurrent: () => boolean = () => true
) {
  const rows: Record<string, unknown>[] = [];
  let cursor: { at: string; id: string } | null = null;
  let status: MarketingSourceCoverage['status'] = 'limited';
  let reason = 'The report reached its 10,000-record or 20-page limit.';
  let error: { message: string } | null = null;
  for (let page = 0; page < 20; page += 1) {
    if (!isCurrent()) {
      throw new Error('Marketing report scope changed. Refresh the workspace.');
    }
    // The final page includes one sentinel. Exact counts distinguish a short
    // server-capped page from exhaustion; a missing count cannot prove coverage.
    const limit = page === 19 ? 501 : 500;
    let query = supabase.from(table).select('*', { count: 'exact' })
      .lt(creationColumn, to)
      .order(creationColumn, { ascending: false }).order('id', { ascending: false })
      .limit(limit);
    if (cursor) {
      query = query.or(`${creationColumn}.lt.${cursor.at},and(${creationColumn}.eq.${cursor.at},id.lt.${cursor.id})`);
    }
    let result;
    try {
      result = await query;
    } catch {
      result = { data: null, error: { message: 'The source could not be read.' }, count: null };
    }
    if (!isCurrent()) {
      throw new Error('Marketing report scope changed. Refresh the workspace.');
    }
    if (result.error || !Array.isArray(result.data)) {
      error = result.error ?? { message: 'The report source returned an invalid response.' };
      status = rows.length ? 'limited' : 'unavailable';
      reason = 'The source could not be fully read. Refresh to retry.';
      break;
    }
    const batch = result.data as Record<string, unknown>[];
    const remaining = 10000 - rows.length;
    const seen = new Set(rows.map((row) => row.id));
    if (batch.some((row) => seen.has(row.id))) {
      reason = 'The source repeated a page; completeness could not be established.';
      break;
    }
    rows.push(...batch.slice(0, remaining));
    if (!batch.length || (result.count !== null && result.count !== undefined && result.count <= batch.length && batch.length <= remaining)) {
      status = 'complete';
      reason = 'All visible current records created before the report cutoff were read.';
      break;
    }
    if (batch.length > remaining) {
      break;
    }
    const last = batch.at(-1)!;
    const at = last[creationColumn];
    if (typeof at !== 'string' || !/^\d{4}-\d\d-\d\dT[\d:.]+(?:Z|[+-]\d\d:\d\d)$/.test(at)
      || typeof last.id !== 'string' || !/^[0-9a-f-]{36}$/i.test(last.id)) {
      reason = 'The source did not supply a stable continuation key.';
      break;
    }
    cursor = { at, id: last.id };
  }
  return {
    data: rows,
    // Reporting failures preserve other Studio sections, with explicit coverage.
    error: null,
    coverage: { from: null, to, observedAt: new Date().toISOString(), status, reason } satisfies MarketingSourceCoverage,
    sourceError: error
  };
}

export function marketingSourcesComplete(
  coverage: Record<string, MarketingSourceCoverage> | undefined,
  sources: string[]
): boolean {
  return sources.every((source) => coverage?.[source]?.status === 'complete');
}
